import { useMemo } from "react";
import { analyzeProject, type GenerationStats, type PfpProject, type Tier } from "@/pfp/generator";
import { cn } from "@/lib/cn";

const TIER_COLORS: Record<Tier, string> = {
  legendary: "text-amber-500 dark:text-amber-400",
  rare: "text-fuchsia-600 dark:text-fuchsia-400",
  uncommon: "text-cyan-700 dark:text-cyan-400",
  common: "text-muted-foreground",
};

const TIER_LABELS: Record<Tier, string> = {
  legendary: "legendary",
  rare: "rare",
  uncommon: "uncommon",
  common: "common",
};

function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div className={cn("h-full transition-all", color)} style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
    </div>
  );
}

const TIER_BAR: Record<Tier, string> = {
  legendary: "bg-amber-500",
  rare: "bg-fuchsia-500",
  uncommon: "bg-cyan-500",
  common: "bg-foreground/40",
};

export function StatsPanel({ project, mintCount }: { project: PfpProject; mintCount: number }) {
  const stats: GenerationStats = useMemo(
    () => analyzeProject(project, mintCount, 1000),
    [project, mintCount],
  );

  if (project.layers.length === 0 || project.layers.every((l) => l.traits.length === 0)) {
    return (
      <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        Stats appear here once you add layers and traits.
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div>
        <h3 className="mb-1 text-sm font-semibold">Generation forecast</h3>
        <p className="text-xs text-muted-foreground">
          Sampled {stats.sampleSize.toLocaleString()} runs. Numbers below are projected counts for {mintCount} tokens.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <Stat label="Possible combos" value={stats.totalPossibleCombos.toLocaleString()} />
        <Stat label="Reachable combos" value={`${stats.effectiveCombos.toLocaleString()}`} />
        <Stat label="Acceptance rate" value={`${(stats.successRate * 100).toFixed(0)}%`} />
        <Stat label="Tokens to mint" value={mintCount.toString()} />
      </div>

      {stats.warnings.length > 0 && (
        <div className="space-y-1">
          {stats.warnings.map((w, i) => (
            <div
              key={i}
              className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-300"
            >
              ⚠ {w}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {stats.perLayer.map((layer) => (
          <div key={layer.layerId}>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {layer.layerName}
            </div>
            <div className="space-y-1.5">
              {layer.traits.map((t) => (
                <div key={t.traitId} className="grid grid-cols-[1fr_60px_50px_60px] items-center gap-2 text-[11px]">
                  <span className="truncate">{t.traitName}</span>
                  <Bar pct={t.probability * 100} color={TIER_BAR[t.tier]} />
                  <span className="text-right tabular-nums text-muted-foreground">
                    {(t.probability * 100).toFixed(1)}%
                  </span>
                  <span className={cn("text-right tabular-nums", TIER_COLORS[t.tier])}>
                    ≈{t.expectedCount} {TIER_LABELS[t.tier].slice(0, 3)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {stats.rarestCombos.length > 0 && stats.effectiveCombos > stats.perLayer.length && (
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Rarest combinations
          </div>
          <div className="space-y-1">
            {stats.rarestCombos.slice(0, 3).map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
                <span className="truncate">
                  {Object.entries(c.comboLabels).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                </span>
                <span className="whitespace-nowrap text-muted-foreground">
                  {(c.probability * 100).toFixed(1)}% · ≈{c.expectedCount}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium tabular-nums">{value}</div>
    </div>
  );
}
